import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLanguageOpen(false);
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg backdrop-blur-sm' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/1c1efe20ab982279d0e71dac7f6c0a80/0bce3b64772fbbd5c34a483b40aa78a9.png"
                alt="Swasthya Ayurvedic Retreat Logo"
                className="h-12 w-auto cursor-pointer"
              />
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            {[
              { name: t('nav.home'), id: 'home' },
              { name: t('nav.about'), id: 'about' },
              { name: t('nav.retreat_details'), id: 'retreat-details' },
              { name: t('nav.experiences'), id: 'experiences' },
              { name: t('nav.schedule'), id: 'schedule' },
              { name: t('nav.reserve'), id: 'reserve' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`font-medium transition-colors duration-300 hover:text-primary-500 cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {item.name}
              </button>
            ))}

            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className={`flex items-center space-x-2 font-medium transition-colors duration-300 hover:text-primary-500 cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                <span>{currentLanguage.flag}</span>
                <span>{currentLanguage.name}</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isLanguageOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isLanguageOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors duration-200 flex items-center space-x-3 cursor-pointer ${
                        i18n.language === lang.code ? 'bg-gray-50 text-primary-500' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span className="font-medium">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <i className={`ri-menu-line text-2xl cursor-pointer transition-colors duration-300 ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}></i>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;